import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import TabNavigation from '../../components/ui/TabNavigation';
import PrivacyToggle from '../../components/ui/PrivacyToggle';
import { useAuth } from '../../contexts/AuthContext';

import QuickActionFloat from '../../components/ui/QuickActionFloat';
import MoodSelector from './components/MoodSelector';
import EnergyLevelSlider from './components/EnergyLevelSlider';
import EncounterTypeSelector from './components/EncounterTypeSelector';
import EmotionalStateSelector from './components/EmotionalStateSelector';
import TimingFlexibility from './components/TimingFlexibility';
import MoodHistory from './components/MoodHistory';
import QuickMoodPresets from './components/QuickMoodPresets';
import CompatibilityAlert from './components/CompatibilityAlert';
import PrivacySettings from './components/PrivacySettings';
import notificationService from '../../services/notificationService';
import realtimePresenceService from '../../services/realtimePresenceService';

const MoodTracker = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [selectedMood, setSelectedMood] = useState('moderate');
  const [energyLevel, setEnergyLevel] = useState(3);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedTiming, setSelectedTiming] = useState('flexible');
  const [showCompatibilityAlert, setShowCompatibilityAlert] = useState(true);
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [partnerMood, setPartnerMood] = useState(null);

  // Real-time partner mood subscription
  useEffect(() => {
    if (!userProfile?.partner_id) return;

    const partnerId = userProfile?.partner_id;

    // Fetch initial partner mood
    const loadPartnerMood = async () => {
      const presenceData = await realtimePresenceService?.fetchPartnerPresence(partnerId);
      if (presenceData?.currentMood) {
        setPartnerMood(presenceData?.currentMood);
      }
    };

    loadPartnerMood();

    // Subscribe to real-time mood changes
    const channel = realtimePresenceService?.subscribeToPartnerMood(
      partnerId,
      (moodData) => {
        setPartnerMood(moodData?.currentMood);
        console.log('Partner mood updated:', moodData?.currentMood);
      }
    );

    // Cleanup subscription
    return () => {
      if (channel) {
        realtimePresenceService?.unsubscribe(`partner_mood_${partnerId}`);
      }
    };
  }, [userProfile?.partner_id]);

  const handleTypeToggle = (typeId) => {
    setSelectedTypes(prev => 
      prev?.includes(typeId)
        ? prev?.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleStateToggle = (stateId) => {
    setSelectedStates(prev =>
      prev?.includes(stateId)
        ? prev?.filter(id => id !== stateId)
        : [...prev, stateId]
    );
  };

  const handlePresetSelect = (preset) => {
    setSelectedMood(preset?.mood);
    setEnergyLevel(preset?.energy);
    setSelectedTypes(preset?.types);
    setSelectedTiming(preset?.timing);
  };

  const handleSaveMood = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setShowCompatibilityAlert(true);
      
      // Check for mood compatibility and trigger notification
      checkMoodCompatibility();
    }, 1000);
  };

  const checkMoodCompatibility = () => {
    // Use real-time partner mood data
    const compatibilityScore = calculateCompatibilityScore(selectedMood, partnerMood, energyLevel);
    
    if (compatibilityScore >= 80) {
      // Trigger push notification for high compatibility
      notificationService?.notifyMoodCompatibility(
        compatibilityScore,
        partnerMood,
        selectedMood
      );
    }
  };

  const calculateCompatibilityScore = (userMood, partnerMood, energy) => {
    // Simple compatibility calculation
    let score = 70;
    if (userMood === partnerMood) score += 20;
    if (energy >= 3) score += 10;
    return Math.min(score, 100);
  };

  const handleScheduleEncounter = () => {
    navigate('/intimacy-scheduler');
  };

  const handleSendMessage = () => {
    navigate('/ai-communication-hub');
  };

  return (
    <div className="min-h-screen bg-background">
      <TabNavigation />
      <PrivacyToggle />
      <QuickActionFloat />

      <div className="pt-[60px] pb-[72px] lg:pt-[76px] lg:pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground">
                Mood Tracker
              </h1>
              <button
                onClick={() => setShowPrivacySettings(true)}
                className="flex items-center space-x-2 px-3 py-2 rounded-md border border-border bg-card hover:bg-muted transition-therapeutic"
              >
                <Icon name="Settings" size={18} color="var(--color-muted-foreground)" />
                <span className="text-sm font-medium text-foreground hidden sm:inline">
                  Privacy
                </span>
              </button>
            </div>
            <p className="text-base md:text-lg text-muted-foreground max-measure">
              Share your current mood and preferences privately. The AI will help match you with your partner when the timing is right.
            </p>
          </div>

          {showCompatibilityAlert && (
            <div className="mb-6">
              <CompatibilityAlert
                isVisible={showCompatibilityAlert}
                onDismiss={() => setShowCompatibilityAlert(false)}
                onViewDetails={handleScheduleEncounter}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-card rounded-lg shadow-therapeutic p-4 md:p-6 border border-border">
                <QuickMoodPresets onPresetSelect={handlePresetSelect} />
              </div>

              <div className="bg-card rounded-lg shadow-therapeutic p-4 md:p-6 border border-border space-y-6">
                <MoodSelector
                  selectedMood={selectedMood}
                  onMoodSelect={setSelectedMood}
                />

                <div className="pt-6 border-t border-border">
                  <EnergyLevelSlider
                    energyLevel={energyLevel}
                    onEnergyChange={setEnergyLevel}
                  />
                </div>

                <div className="pt-6 border-t border-border">
                  <EncounterTypeSelector
                    selectedTypes={selectedTypes}
                    onTypeToggle={handleTypeToggle}
                  />
                </div>

                <div className="pt-6 border-t border-border">
                  <EmotionalStateSelector
                    selectedStates={selectedStates}
                    onStateToggle={handleStateToggle}
                  />
                </div>

                <div className="pt-6 border-t border-border">
                  <TimingFlexibility
                    selectedTiming={selectedTiming}
                    onTimingSelect={setSelectedTiming}
                  />
                </div>

                <div className="pt-6 border-t border-border flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleSaveMood}
                    disabled={isSaving}
                    className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-therapeutic font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <Icon name="Loader2" size={20} color="currentColor" className="animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Check" size={20} color="currentColor" />
                        <span>Update Current Mood</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => navigate('/ai-communication-hub')}
                    className="flex items-center justify-center space-x-2 px-6 py-3 rounded-md border border-border bg-card text-foreground hover:bg-muted transition-therapeutic font-medium"
                  >
                    <Icon name="MessageCircle" size={20} color="currentColor" />
                    <span>Share Details</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-card rounded-lg shadow-therapeutic p-4 md:p-6 border border-border">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10">
                    <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 className="text-base font-heading font-semibold text-foreground">
                      Mood Insights
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Last 7 days
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-md bg-muted">
                    <span className="text-sm text-foreground">Average Energy</span>
                    <span className="text-sm font-medium text-primary">3.4/5</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-md bg-muted">
                    <span className="text-sm text-foreground">Most Common Mood</span>
                    <span className="text-sm font-medium text-primary">Moderate</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-md bg-muted">
                    <span className="text-sm text-foreground">Compatibility Matches</span>
                    <span className="text-sm font-medium text-accent">12</span>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg shadow-therapeutic p-4 md:p-6 border border-border">
                <MoodHistory historyData={[]} />
              </div>

              <div className="bg-card rounded-lg shadow-therapeutic p-4 md:p-6 border border-border">
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-md bg-accent/10 flex-shrink-0">
                    <Icon name="Lightbulb" size={20} color="var(--color-accent)" />
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-semibold text-foreground mb-1">
                      AI Tip
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Your mood patterns suggest you're most receptive to intimacy on weekday evenings. Consider scheduling encounters during these times.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PrivacySettings
        isOpen={showPrivacySettings}
        onClose={() => setShowPrivacySettings(false)}
      />
    </div>
  );
};

export default MoodTracker;